/**
 * Visual clock (W1-14, A§8, R§6.4): maps performance time (the animation-frame timestamp) to the AudioContext time
 * that is AUDIBLE at that moment, smoothed so that the playhead advances without jitter between frames.
 *
 * Source, best first (feature-detected per call):
 *  1. `getOutputTimestamp()` (Chrome, Firefox 70+, Safari 14.1+): `{contextTime, performanceTime}` is the context
 *     frame currently being heard and the performance time at which it is heard, so
 *     `audible(p) = contextTime + (p - performanceTime) / 1000` already includes the output latency. It is used
 *     only when sane (both fields > 0, the context running, the stamp less than 1 s old).
 *  2. `currentTime` interpolated with performance time between its render-quantum steps, minus `outputLatency`
 *     (Chrome 102+, Firefox 70+), else minus `baseLatency`, else minus nothing.
 * The user's calibration (LatencySettings.calibrationMs) is subtracted by the caller; it shifts the visual clock
 * only (D§5.2), never the audio or the chart.
 *
 * Smoothing: a first-order phase-locked estimate with slope 1. Each frame predicts `last + dt`, then moves 10 % of
 * the way to the raw reading; readings more than 50 ms ahead snap forward, readings more than 50 ms behind (an
 * audio stall) freeze the clock until the audio catches up. The output never runs backwards.
 */

import { fallbackLatencySec, readLatency } from './latency';

/** The part of an AudioContext the clock reads. */
export interface ClockSourceContext {
  readonly currentTime: number;
  readonly state: string;
}

/** Readings further apart than this snap instead of being smoothed (s). */
export const CLOCK_SNAP_SEC = 0.05;
/** Fraction of the error corrected per evaluation. */
export const CLOCK_GAIN = 0.1;
/** Output timestamps older than this (ms) are considered stale. */
const STAMP_MAX_AGE_MS = 1000;
/** Max extrapolation of `currentTime` between observed steps (s). */
const MAX_INTERPOLATION_SEC = 0.1;

interface Stamp {
  readonly contextTime: number;
  readonly performanceTime: number;
}

/** A sane output timestamp of a running context, or null (see the module header). */
export function outputStamp(ctx: ClockSourceContext, perfMs: number): Stamp | null {
  if (ctx.state !== 'running') return null;
  const fn = (ctx as { readonly getOutputTimestamp?: unknown }).getOutputTimestamp;
  if (typeof fn !== 'function') return null;
  const raw = (fn as () => unknown).call(ctx) as {
    readonly contextTime?: unknown;
    readonly performanceTime?: unknown;
  } | null;
  const contextTime = raw?.contextTime;
  const performanceTime = raw?.performanceTime;
  if (typeof contextTime !== 'number' || typeof performanceTime !== 'number') return null;
  if (!(contextTime > 0) || !(performanceTime > 0)) return null;
  if (Math.abs(perfMs - performanceTime) > STAMP_MAX_AGE_MS) return null;
  return { contextTime, performanceTime };
}

export class VisualClock {
  private readonly perfNow: () => number;
  private ctx: ClockSourceContext | null = null;
  private lastCt = NaN;
  private lastCtPerf = 0;
  private lastPerf = NaN;
  private lastOut = 0;

  /** `perfNow` returns the current performance time in ms (used to time `currentTime` steps). */
  constructor(perfNow: () => number) {
    this.perfNow = perfNow;
  }

  /** Forget the smoothing state (new context). */
  reset(): void {
    this.ctx = null;
    this.lastCt = NaN;
    this.lastCtPerf = 0;
    this.lastPerf = NaN;
    this.lastOut = 0;
  }

  private bind(ctx: ClockSourceContext): void {
    if (ctx !== this.ctx) {
      this.reset();
      this.ctx = ctx;
    }
  }

  /** `currentTime` interpolated to performance time `perfMs` (no latency compensation). */
  private interpolated(ctx: ClockSourceContext, perfMs: number): number {
    const ct = ctx.currentTime;
    if (ct !== this.lastCt) {
      this.lastCt = ct;
      this.lastCtPerf = this.perfNow();
    }
    if (ctx.state !== 'running') return ct;
    const dt = (perfMs - this.lastCtPerf) / 1000;
    return ct + Math.max(-MAX_INTERPOLATION_SEC, Math.min(MAX_INTERPOLATION_SEC, dt));
  }

  /** Unsmoothed audible context time at performance time `perfMs`. */
  rawAudible(ctx: ClockSourceContext, perfMs: number): number {
    this.bind(ctx);
    const stamp = outputStamp(ctx, perfMs);
    const interpolated = this.interpolated(ctx, perfMs);
    if (stamp !== null) {
      const audible = stamp.contextTime + (perfMs - stamp.performanceTime) / 1000;
      // A frame cannot be audible long before it is rendered: such a stamp is garbage, use the fallback.
      if (audible <= interpolated + MAX_INTERPOLATION_SEC) return audible;
    }
    return interpolated - fallbackLatencySec(readLatency(ctx));
  }

  /** Smoothed, monotonic audible context time for the frame at `perfMs`. */
  audible(ctx: ClockSourceContext, perfMs: number): number {
    this.bind(ctx);
    if (perfMs === this.lastPerf) return this.lastOut;
    const raw = this.rawAudible(ctx, perfMs);
    if (Number.isNaN(this.lastPerf)) {
      this.lastPerf = perfMs;
      this.lastOut = raw;
      return raw;
    }
    if (perfMs < this.lastPerf) return this.lastOut;
    const running = ctx.state === 'running';
    const predicted = running ? this.lastOut + (perfMs - this.lastPerf) / 1000 : this.lastOut;
    const err = raw - predicted;
    let out: number;
    if (!running) out = raw;
    else if (err > CLOCK_SNAP_SEC) out = raw;
    else if (err < -CLOCK_SNAP_SEC) out = Math.max(this.lastOut, raw);
    else out = predicted + err * CLOCK_GAIN;
    out = Math.max(out, this.lastOut);
    this.lastPerf = perfMs;
    this.lastOut = out;
    return out;
  }

  /** Performance time (ms) at which context time `ctxTime` is audible (unsmoothed inverse of rawAudible). */
  perfAt(ctx: ClockSourceContext, ctxTime: number): number {
    this.bind(ctx);
    const now = this.perfNow();
    const stamp = outputStamp(ctx, now);
    if (stamp !== null) return stamp.performanceTime + (ctxTime - stamp.contextTime) * 1000;
    const audibleNow = this.interpolated(ctx, now) - fallbackLatencySec(readLatency(ctx));
    return now + (ctxTime - audibleNow) * 1000;
  }
}
